import { useState } from 'react'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { InputField } from '@/shared/ui'

type VideosShellHeaderProps = {
  isFilterPanelOpen: boolean
  isSidebarExpanded: boolean
  isDesktopViewport: boolean
  showFilterControl?: boolean
  showSearchControl?: boolean
  searchValue?: string
  onSearchValueChange?: (value: string) => void
  onMenuToggle: () => void
  onFilterClick: () => void
  onLogout?: () => void
}

const COMMUNITY_BOT_URL = 'https://t.me/epicgrowth_com_bot'

const headerStyle = {
  minHeight: '76px',
  backgroundColor: '#212121',
  boxShadow: '0px 0px 4px rgba(44, 13, 66, 0.1)',
}

const headerFrameStyle = {
  padding: '16px 24px',
}

const brandStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.xsSemibold, {
    fontSize: '12px',
    lineHeight: '18px',
  }),
}

const searchInputStyle = {
  height: '40px',
  backgroundColor: designTokens.colors.background.white,
  borderRadius: '9999px',
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} #D9D9D9`,
  paddingRight: '16px',
  color: 'rgba(10, 10, 10, 0.7)',
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.base),
}

const roundButtonStyle = {
  width: '44px',
  height: '44px',
  borderRadius: '9999px',
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} #2C2C2C`,
  backgroundColor: '#2C2C2C',
}

const communityButtonStyle = {
  minHeight: '35px',
  borderRadius: '9999px',
  padding: '9px 20px',
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} #404040`,
  backgroundColor: '#404040',
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.base, {
    fontSize: '15px',
    lineHeight: '100%',
    letterSpacing: '-0.15px',
  }),
}

const logoutButtonStyle = {
  height: '32px',
  borderRadius: '9999px',
  padding: '0 8px',
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.base, {
    fontSize: '13px',
    lineHeight: '100%',
    letterSpacing: '-0.15px',
  }),
}

const BurgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 7H20M4 12H20M4 17H20" stroke="#FEFEFE" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const SlidersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M3 5H8M12 5H17M3 10H5M9 10H17M3 15H11M15 15H17"
      stroke="#F5F5F5"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="10" cy="5" r="2" stroke="#F5F5F5" strokeWidth="1.5" />
    <circle cx="7" cy="10" r="2" stroke="#F5F5F5" strokeWidth="1.5" />
    <circle cx="13" cy="15" r="2" stroke="#F5F5F5" strokeWidth="1.5" />
  </svg>
)

const SearchToggleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="5.75" stroke="#F5F5F5" strokeWidth="1.5" />
    <path d="M13.5 13.5L16.75 16.75" stroke="#F5F5F5" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

export function VideosShellHeader({
  isFilterPanelOpen,
  isSidebarExpanded,
  isDesktopViewport,
  showFilterControl = true,
  showSearchControl = true,
  searchValue,
  onSearchValueChange,
  onMenuToggle,
  onFilterClick,
  onLogout,
}: VideosShellHeaderProps) {
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false)
  const hasSearchText = Boolean(searchValue?.trim().length)
  const isSearchExpanded = isDesktopViewport
    ? isSearchFocused || hasSearchText
    : isMobileSearchOpen || isSearchFocused || hasSearchText
  const showInlineSearch = showSearchControl && (isDesktopViewport || isSearchExpanded)
  const showSearchToggle = showSearchControl && !isDesktopViewport && !isSearchExpanded
  const showBrand = isDesktopViewport
  const headerGap = isDesktopViewport ? '24px' : '12px'
  const controlsGap = isDesktopViewport ? '24px' : '12px'
  const leftGroupStyle = {
    flex: '1 1 auto',
    minWidth: 0,
    gap: headerGap,
  }
  const controlsGroupStyle = {
    flex: isDesktopViewport ? '0 1 auto' : isSearchExpanded ? '1 1 auto' : '0 0 auto',
    minWidth: 0,
    gap: controlsGap,
    marginLeft: isDesktopViewport ? undefined : 'auto',
  }

  return (
    <header className="fixed inset-x-0 top-0 z-40 w-full" style={headerStyle}>
      <div className="flex w-full items-center justify-between gap-4" style={headerFrameStyle}>
        <div className="flex min-w-0 items-center" style={leftGroupStyle}>
          <button
            type="button"
            className="inline-flex h-6 w-6 cursor-pointer items-center justify-center"
            aria-label={
              isDesktopViewport
                ? isSidebarExpanded
                  ? 'Свернуть меню'
                  : 'Развернуть меню'
                : 'Открыть меню'
            }
            aria-expanded={isSidebarExpanded}
            onClick={onMenuToggle}
          >
            <BurgerIcon />
          </button>
          {showBrand ? <span style={brandStyle}>epic growth</span> : null}
          <div
            className="flex min-w-0 items-center"
            style={controlsGroupStyle}
            data-testid="videos-shell-header-controls"
          >
            {showInlineSearch ? (
              <InputField
                type="search"
                placeholder="Поиск"
                aria-label="Поиск"
                variant="search"
                value={searchValue}
                onChange={(event) => onSearchValueChange?.(event.target.value)}
                containerStyle={{
                  width: isDesktopViewport
                    ? isSearchExpanded
                      ? 'min(520px, 52vw)'
                      : '176px'
                    : '100%',
                  flex: isDesktopViewport ? undefined : '1 1 auto',
                  transition: 'width 220ms ease',
                }}
                inputClassName="w-full bg-transparent outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                style={searchInputStyle}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => {
                  setIsSearchFocused(false)
                  if (!isDesktopViewport && !searchValue?.trim().length) {
                    setIsMobileSearchOpen(false)
                  }
                }}
              />
            ) : null}
            {showSearchToggle ? (
              <button
                type="button"
                aria-label="Открыть поиск"
                className="inline-flex cursor-pointer items-center justify-center"
                onClick={() => {
                  setIsMobileSearchOpen(true)
                }}
                style={roundButtonStyle}
              >
                <SearchToggleIcon />
              </button>
            ) : null}
            {showFilterControl ? (
              <button
                type="button"
                aria-label="Фильтры"
                className="inline-flex cursor-pointer items-center justify-center"
                onClick={onFilterClick}
                aria-expanded={isFilterPanelOpen}
                style={{
                  ...roundButtonStyle,
                  backgroundColor: isFilterPanelOpen ? '#404040' : '#2C2C2C',
                }}
              >
                <SlidersIcon />
              </button>
            ) : null}
          </div>
        </div>
        {isDesktopViewport ? (
          <div className="hidden shrink-0 items-center gap-3 md:flex">
            <a
              href={COMMUNITY_BOT_URL}
              target="_blank"
              rel="noreferrer"
              className="hidden cursor-pointer items-center justify-center whitespace-nowrap no-underline lg:inline-flex"
              style={communityButtonStyle}
            >
              Сообщество Epic Growth
            </a>
            <button
              type="button"
              className="inline-flex cursor-pointer items-center justify-center"
              style={logoutButtonStyle}
              onClick={onLogout}
            >
              Выйти
            </button>
          </div>
        ) : null}
      </div>
    </header>
  )
}
